import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Search, Loader2, Trash2 } from 'lucide-react';
import { useCreateAssignment } from '@/hooks/use-assignments';
import {
  useSearchDashboardStudents,
  useSearchPracticeSeeds,
} from '@/hooks/use-dashboard';
import { toast } from 'sonner';
import { toToastMessage } from '@/lib/api-error';
import type {
  DashboardStudentSearchItem,
  PracticeSeedSearchItem,
} from '@/types/dashboard';

function formatStudentName(s: DashboardStudentSearchItem | FixedStudent) {
  const name = [s.first_name, s.last_name].filter(Boolean).join(' ');
  return name || s.username || s.email || s.id.slice(0, 8);
}

interface FixedStudent {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  email?: string | null;
}

interface FixedSeed {
  id: string;
  title: string;
  problem_category: string;
  estimated_minutes: number;
  difficulty_level: number;
}

interface AssignPracticeDialogProps {
  /** When set, student picker is hidden and this student is used. */
  fixedStudent?: FixedStudent;
  /** When set, seed picker is hidden and this seed is used. */
  fixedSeed?: FixedSeed;
  /** Trigger button label. Default: "Assign Practice". */
  triggerLabel?: string;
  /** Trigger button variant. Default: "default". */
  triggerVariant?: 'default' | 'outline' | 'secondary' | 'ghost';
  /** Trigger button size. Default: "default". */
  triggerSize?: 'default' | 'sm' | 'lg' | 'icon';
  /** Optional success callback (e.g. refetch parent list). */
  onSuccess?: () => void;
}

export function AssignPracticeDialog({
  fixedStudent,
  fixedSeed,
  triggerLabel = 'Assign Practice',
  triggerVariant = 'default',
  triggerSize = 'default',
  onSuccess,
}: AssignPracticeDialogProps) {
  const [open, setOpen] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const mutation = useCreateAssignment();

  // Student autocomplete state
  const [studentQuery, setStudentQuery] = useState('');
  const [debouncedStudentQuery, setDebouncedStudentQuery] = useState('');
  const [selectedStudent, setSelectedStudent] =
    useState<DashboardStudentSearchItem | null>(null);
  const studentInputRef = useRef<HTMLInputElement>(null);

  // Practice seed autocomplete state
  const [seedQuery, setSeedQuery] = useState('');
  const [debouncedSeedQuery, setDebouncedSeedQuery] = useState('');
  const [selectedSeed, setSelectedSeed] = useState<PracticeSeedSearchItem | null>(null);
  const seedInputRef = useRef<HTMLInputElement>(null);

  // Debounce student search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStudentQuery(studentQuery), 300);
    return () => clearTimeout(timer);
  }, [studentQuery]);

  // Debounce seed search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSeedQuery(seedQuery), 300);
    return () => clearTimeout(timer);
  }, [seedQuery]);

  const { data: studentResults, isLoading: isStudentSearching } =
    useSearchDashboardStudents(debouncedStudentQuery);
  const { data: seedResults, isLoading: isSeedSearching } =
    useSearchPracticeSeeds(debouncedSeedQuery);

  const effectiveStudentID = fixedStudent?.id ?? selectedStudent?.id;
  const effectiveSeedID = fixedSeed?.id ?? selectedSeed?.id;

  const resetState = () => {
    setStudentQuery('');
    setSelectedStudent(null);
    setSeedQuery('');
    setSelectedSeed(null);
    setDueDate('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveStudentID || !effectiveSeedID) return;

    // datetime-local input returns "2026-04-11T15:36" (no timezone).
    const dueDateRfc = dueDate ? new Date(dueDate).toISOString() : undefined;

    mutation.mutate(
      {
        student_id: effectiveStudentID,
        practice_seed_id: effectiveSeedID,
        due_date: dueDateRfc,
      },
      {
        onSuccess: () => {
          toast.success('Assignment created');
          setOpen(false);
          resetState();
          onSuccess?.();
        },
        onError: (err) =>
          toast.error(toToastMessage(err, 'Failed to create assignment')),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant={triggerVariant} size={triggerSize} />}>
        <Plus className="mr-2 h-4 w-4" />
        {triggerLabel}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Practice</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Student: fixed or autocomplete */}
          {fixedStudent ? (
            <div className="space-y-2">
              <Label>Student</Label>
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm font-medium">
                {formatStudentName(fixedStudent)}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Student</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={studentInputRef}
                  value={studentQuery}
                  onChange={(e) => {
                    setStudentQuery(e.target.value);
                    setSelectedStudent(null);
                  }}
                  placeholder="Search by name, email, or username..."
                  className="pl-9"
                />
                {isStudentSearching && (
                  <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              {debouncedStudentQuery.length >= 2 && !selectedStudent && (
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {studentResults && studentResults.length > 0 ? (
                    studentResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => {
                          setSelectedStudent(s);
                          setStudentQuery(formatStudentName(s));
                        }}
                      >
                        <div className="flex-1">
                          <div className="font-medium">{formatStudentName(s)}</div>
                          {(s.username || s.telegram_id) && (
                            <div className="text-xs text-muted-foreground">
                              {s.username ? `@${s.username}` : `ID: ${s.telegram_id}`}
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  ) : !isStudentSearching ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No students found
                    </div>
                  ) : null}
                </div>
              )}
              {selectedStudent && (
                <div className="flex items-center gap-2 rounded-md border bg-accent/50 px-3 py-2 text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{formatStudentName(selectedStudent)}</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setSelectedStudent(null);
                      setStudentQuery('');
                      studentInputRef.current?.focus();
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Practice seed: fixed or autocomplete */}
          {fixedSeed ? (
            <div className="space-y-2">
              <Label>Practice Seed</Label>
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
                <div className="font-medium">{fixedSeed.title}</div>
                <div className="text-xs text-muted-foreground">
                  {fixedSeed.problem_category} &middot; {fixedSeed.estimated_minutes} min &middot; Level{' '}
                  {fixedSeed.difficulty_level}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Practice Seed</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={seedInputRef}
                  value={seedQuery}
                  onChange={(e) => {
                    setSeedQuery(e.target.value);
                    setSelectedSeed(null);
                  }}
                  placeholder="Search by title or category..."
                  className="pl-9"
                />
                {isSeedSearching && (
                  <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              {debouncedSeedQuery.length >= 2 && !selectedSeed && (
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {seedResults && seedResults.length > 0 ? (
                    seedResults.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => {
                          setSelectedSeed(s);
                          setSeedQuery(s.title);
                        }}
                      >
                        <div className="flex-1">
                          <div className="font-medium">{s.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {s.problem_category} &middot; {s.estimated_minutes} min &middot; Level{' '}
                            {s.difficulty_level}
                          </div>
                        </div>
                      </button>
                    ))
                  ) : !isSeedSearching ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No practice seeds found
                    </div>
                  ) : null}
                </div>
              )}
              {selectedSeed && (
                <div className="flex items-center gap-2 rounded-md border bg-accent/50 px-3 py-2 text-sm">
                  <div className="flex-1">
                    <span className="font-medium">{selectedSeed.title}</span>
                    <span className="ml-2 text-muted-foreground">
                      {selectedSeed.problem_category} &middot; {selectedSeed.estimated_minutes} min
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      setSelectedSeed(null);
                      setSeedQuery('');
                      seedInputRef.current?.focus();
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Due date */}
          <div className="space-y-2">
            <Label htmlFor="dueDate">Due Date (optional)</Label>
            <Input
              id="dueDate"
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={!effectiveStudentID || !effectiveSeedID || mutation.isPending}
          >
            {mutation.isPending ? 'Creating...' : 'Create Assignment'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
